import React, { useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Linking,
  Platform,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import { Lora_700Bold } from '@expo-google-fonts/lora';
import {
  DMSans_400Regular,
  DMSans_500Medium,
  DMSans_600SemiBold,
} from '@expo-google-fonts/dm-sans';
import { DynamicTitle } from '../DynamicTitle';
import {
  COMENSAL_APP_DEEP_LINK_REGISTRO_CONFIRMAR,
  urlPareceConfirmacionCorreoSupabase,
} from '../../lib/constants/comensalAuthUrls';

const SITE_HOME = 'https://asgastronomico.com';

export function ComensalCorreoVerificadoPage() {
  const [fontsLoaded] = useFonts({
    Lora_700Bold,
    DMSans_400Regular,
    DMSans_500Medium,
    DMSans_600SemiBold,
  });

  const exito = useMemo(() => urlPareceConfirmacionCorreoSupabase(), []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    let meta = document.querySelector('meta[name="robots"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (created && meta?.parentNode) {
        meta.parentNode.removeChild(meta);
      } else if (meta) {
        meta.removeAttribute('content');
      }
    };
  }, []);

  const abrirApp = () => {
    Linking.openURL(COMENSAL_APP_DEEP_LINK_REGISTRO_CONFIRMAR).catch(() => {});
  };

  const irAlSitio = () => {
    Linking.openURL(SITE_HOME).catch(() => {});
  };

  if (!fontsLoaded) {
    return <View style={styles.screen} />;
  }

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
    >
      <DynamicTitle
        title={
          exito ? 'Correo verificado — As Gastronómico' : 'Confirmar correo — As Gastronómico'
        }
      />

      <View style={styles.card}>
        <View
          style={[styles.iconCircle, exito ? styles.iconCircleOk : styles.iconCircleError]}
        >
          <Ionicons
            name={exito ? 'checkmark' : 'close'}
            size={40}
            color={exito ? '#10b981' : '#dc2626'}
          />
        </View>

        {exito ? (
          <>
            <Text style={styles.title}>Correo verificado</Text>
            <Text style={styles.subtitle}>
              Tu cuenta de comensal quedó confirmada correctamente.
            </Text>
            <Text style={styles.body}>
              Ya puedes volver a la aplicación As Gastronómico e iniciar sesión con tu correo y
              contraseña. Si tenías la app abierta en el paso de verificación, pulsa «Comprobar
              ahora» o espera unos segundos.
            </Text>
            <TouchableOpacity style={styles.primaryBtn} onPress={abrirApp} activeOpacity={0.88}>
              <Text style={styles.primaryBtnText}>Abrir la app</Text>
            </TouchableOpacity>
            <Text style={styles.hint}>
              Si no se abre automáticamente, vuelve a la app manualmente.
            </Text>
            <TouchableOpacity onPress={irAlSitio} activeOpacity={0.7} style={styles.secondaryLink}>
              <Text style={styles.secondaryLinkText}>Ir a asgastronomico.com</Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <Text style={styles.title}>No pudimos confirmar tu correo</Text>
            <Text style={styles.body}>
              El enlace puede haber expirado o ya fue usado. Vuelve a la app, inicia sesión o
              solicita un nuevo correo de verificación.
            </Text>
            <TouchableOpacity style={styles.primaryBtn} onPress={abrirApp} activeOpacity={0.88}>
              <Text style={styles.primaryBtnText}>Volver a la app</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={irAlSitio} activeOpacity={0.7} style={styles.secondaryLink}>
              <Text style={styles.secondaryLinkText}>Ir a asgastronomico.com</Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
    paddingHorizontal: 20,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingVertical: 36,
    paddingHorizontal: 24,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  iconCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  iconCircleOk: {
    backgroundColor: '#ecfdf5',
  },
  iconCircleError: {
    backgroundColor: '#fef2f2',
  },
  title: {
    fontFamily: 'Lora_700Bold',
    fontSize: 26,
    lineHeight: 32,
    color: '#030213',
    textAlign: 'center',
    marginBottom: 10,
  },
  subtitle: {
    fontFamily: 'DMSans_600SemiBold',
    fontSize: 16,
    lineHeight: 24,
    color: '#374151',
    textAlign: 'center',
    marginBottom: 12,
  },
  body: {
    fontFamily: 'DMSans_400Regular',
    fontSize: 15,
    lineHeight: 22,
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 24,
  },
  primaryBtn: {
    backgroundColor: '#A20000',
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 10,
    minWidth: 200,
    alignItems: 'center',
  },
  primaryBtnText: {
    fontFamily: 'DMSans_600SemiBold',
    fontSize: 16,
    color: '#ffffff',
  },
  hint: {
    marginTop: 14,
    fontFamily: 'DMSans_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: '#9ca3af',
    textAlign: 'center',
  },
  secondaryLink: {
    marginTop: 20,
    paddingVertical: 8,
  },
  secondaryLinkText: {
    fontFamily: 'DMSans_500Medium',
    fontSize: 14,
    color: '#6b7280',
    textDecorationLine: 'underline',
  },
});
